// JWT Role Management Utility
export const jwtUtils = {
  /**
   * Decode JWT token and extract user information
   * @param {string} token - The JWT token
   * @returns {object|null} Decoded token payload or null if invalid
   */
  decodeToken: (token) => {
    if (!token) return null;
    
    try {
      // JWT tokens have 3 parts separated by dots
      const parts = token.split('.');
      if (parts.length !== 3) return null;
      
      // Decode the payload (second part)
      const payload = parts[1];
      
      // Add padding if needed for base64 decoding
      const paddedPayload = payload.padEnd(payload.length + (4 - payload.length % 4) % 4, '=');
      
      // Decode base64
      const decodedPayload = atob(paddedPayload);
      
      // Parse JSON
      return JSON.parse(decodedPayload);
    } catch (error) {
      console.error('Error decoding JWT token:', error);
      return null;
    }
  },

  /**
   * Get current user information from stored access token
   * @returns {object|null} User information or null if not authenticated
   */
  getCurrentUser: () => {
    const token = localStorage.getItem('accessToken');
    if (!token) return null;
    
    return jwtUtils.decodeToken(token);
  },

  /**
   * Check if current user has admin role
   * @returns {boolean} True if user is admin or super_admin, false otherwise
   */
  isAdmin: () => {
    const user = jwtUtils.getCurrentUser();
    if (!user) return false;
    
    // Check various possible role claim names
    const role = user.role || user.roles || user.user_role || user.userRole || user.Role;
    
    if (Array.isArray(role)) {
      return role.some(r => r.toLowerCase() === 'admin' || r.toLowerCase() === 'super_admin');
    }
    
    return role && (role.toLowerCase() === 'admin' || role.toLowerCase() === 'super_admin');
  },

  /**
   * Get user role(s)
   * @returns {string|array|null} User role(s) or null if not found
   */
  getUserRole: () => {
    const user = jwtUtils.getCurrentUser();
    if (!user) return null;
    
    return user.role || user.roles || user.user_role || user.userRole || user.Role;
  },

  /**
   * Get user information for display
   * @returns {object|null} User display information
   */
  getUserInfo: () => {
    const user = jwtUtils.getCurrentUser();
    if (!user) return null;
    
    return {
      id: user.sub || user.userId || user.id,
      email: user.email || user.user_email,
      name: user.name || user.username || user.user_name,
      role: jwtUtils.getUserRole(),
      isAdmin: jwtUtils.isAdmin(),
      exp: user.exp, // Token expiration
      iat: user.iat  // Token issued at
    };
  },

  /**
   * Check if token is expired
   * @returns {boolean} True if token is expired
   */
  isTokenExpired: () => {
    const user = jwtUtils.getCurrentUser();
    if (!user || !user.exp) return true;
    
    const currentTime = Math.floor(Date.now() / 1000);
    return user.exp < currentTime;
  },

  /**
   * Check if user is authenticated and not expired
   * @returns {boolean} True if user is authenticated with valid token
   */
  isAuthenticated: () => {
    const token = localStorage.getItem('accessToken');
    if (!token) return false;
    
    return !jwtUtils.isTokenExpired();
  },

  /**
   * Get stored refresh token
   * @returns {string|null} Refresh token or null if not found
   */
  getRefreshToken: () => {
    return localStorage.getItem('refreshToken');
  },

  /**
   * Get stored token family
   * @returns {string|null} Token family or null if not found
   */
  getTokenFamily: () => {
    return localStorage.getItem('tokenFamily');
  },

  /**
   * Check if token refresh is needed (within 5 minutes of expiry)
   * @returns {boolean} True if token should be refreshed soon
   */
  shouldRefreshToken: () => {
    const user = jwtUtils.getCurrentUser();
    if (!user || !user.exp) return true;
    
    const currentTime = Math.floor(Date.now() / 1000);
    const fiveMinutes = 5 * 60; // 5 minutes in seconds
    
    return user.exp - currentTime < fiveMinutes;
  }
};

// Export for use in components
export default jwtUtils;
